import { formatDateKey, plainTextFromHtml } from '@/js/core/utils/formatters';
import {
  CALENDAR_EVENT_DISPLAY_MODES,
  calendarEventDateKey,
  calendarEventEndDateKey,
  calendarEventSortTimestamp,
} from '@/js/core/utils/calendar-event-time';
import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone';
import { calendarEventRouteSlugFromValues } from '@/js/core/utils/calendar-event-route';
import { withReturnPath } from '@/js/core/utils/return-path.js';

export const EVENT_FILTER_GROUPS = {
  'Academic Calendar': ['Important Date', 'Deadline', 'Holiday'],
  Events: [
    'Reception',
    'Lecture',
    'Panel Discussion',
    'Student Group Event',
    'Workshop',
    'Exhibition',
    'Meeting',
    'Colloquium',
    'Review/Pin-up',
    'Alumni Event',
    'Award Ceremony',
    'Screening',
    'Symposium',
    'Social Event',
    'Conference',
    'Commencement',
  ],
  Reviews: [
    'Midterm Review',
    'Thesis Review',
    'Core Studio Review',
    'Option Studio Review',
    'Independent Project Review',
    'DDes Dissertation Defense',
    'PhD Dissertation Defense',
    'Open Project Review',
    'Studio Pin-Up',
  ],
};

export const CALENDAR_EVENT_NAVIGATION_MODES = {
  modal: 'modal',
  link: 'link',
};

/**
 * Return the top-level filter group for an event category.
 */
export function getEventFilterGroup(
  category,
  filterGroups = EVENT_FILTER_GROUPS,
  fallbackGroup = 'Other'
) {
  const normalizedCategory = normalizeCalendarCategory(category);

  return (
    Object.keys(filterGroups).find(group =>
      filterGroups[group].some(option => String(option).trim().toLowerCase() === normalizedCategory)
    ) || fallbackGroup
  );
}

/**
 * Build event view grouping metadata from Calendar filter options.
 */
export function eventFilterGroupsFromOptions(filterGroups = []) {
  const groups = {
    'Academic Calendar': categoryOptionLabels(filterGroups, 'academic_calendar'),
    Events: categoryOptionLabels(filterGroups, 'events'),
    Reviews: categoryOptionLabels(filterGroups, 'reviews'),
  };

  return Object.fromEntries(
    Object.entries(groups).map(([title, values]) => [
      title,
      values.length ? values : EVENT_FILTER_GROUPS[title],
    ])
  );
}

/**
 * Decide whether a calendar card should open the event detail modal.
 */
export function isCalendarEventLinkable(event, timezone = DEFAULT_TIMEZONE) {
  if (!event) {
    return false;
  }

  return hasEventDetail(event) || hasLivestream(event) || hasComplexSchedule(event, timezone);
}

/**
 * Report whether the event should show the Advance Registration label.
 */
export function hasAdvanceRegistration(event) {
  return event?.livestream_details?.has_advance_registration === true;
}

/**
 * Report whether the event should show the Livestream label.
 */
export function hasLivestream(event) {
  return event?.livestream_details?.has_livestream === true;
}

/**
 * Report whether the event takes place on Zoom.
 *
 * Derived from the event's own Services API fields (see CalendarEventPresenter),
 * so it holds for every event — not only those with a provisioned livestream row.
 */
export function hasZoom(event) {
  return event?.has_zoom === true;
}

/**
 * Join a location string with a "Zoom" suffix when the event is on Zoom.
 */
export function locationWithZoom(location, eventHasZoom) {
  return [location, eventHasZoom ? 'Zoom' : null].filter(Boolean).join(' & ');
}

/**
 * Return a safe registration URL for event CTA links.
 */
export function registrationUrlForEvent(event) {
  const url = String(event?.registration_link || '').trim();

  if (!url.includes('http')) {
    return '';
  }

  try {
    const parsedUrl = new URL(url);

    return ['http:', 'https:'].includes(parsedUrl.protocol) ? url : '';
  } catch {
    return '';
  }
}

/**
 * Return the canonical frontend URL for an event detail page.
 */
export function calendarEventHref(event) {
  if (!event) {
    return '';
  }

  // Link to the clicked occurrence's own slug (its Services API id) so the modal
  // opens scoped to that occurrence's group, not the whole aggregated event.
  const occurrenceServiceApiId = event.visible_occurrences?.[0]?.gsd_service_api_id;
  if (occurrenceServiceApiId) {
    const slug = calendarEventRouteSlugFromValues(occurrenceServiceApiId, event.title);

    return slug ? `/calendar/${encodeURIComponent(slug)}` : '';
  }

  if (event.path) {
    return event.path;
  }

  const eventPageSlug = event.event_page_slug || event.gsd_service_api_id;

  return eventPageSlug ? `/calendar/${encodeURIComponent(eventPageSlug)}` : '';
}

/**
 * Return semantic wrapper attributes for a calendar event navigation target.
 */
export function calendarEventNavigation(
  event,
  { mode = CALENDAR_EVENT_NAVIGATION_MODES.modal, timezone = DEFAULT_TIMEZONE } = {}
) {
  const isLinkable = isCalendarEventLinkable(event, timezone);
  const usesLink = mode === CALENDAR_EVENT_NAVIGATION_MODES.link;
  const canonicalHref = calendarEventHref(event);
  const href = usesLink ? withReturnPath(canonicalHref, window.location.pathname) : canonicalHref;
  const canLink = isLinkable && href !== '';

  return {
    tag: canLink ? 'a' : 'article',
    href: canLink ? href : undefined,
    isInteractive: canLink,
  };
}

function hasEventDetail(event) {
  return Boolean(
    textValue(event.content) ||
      textValue(event.description) ||
      textValue(event.calendar_listing_desc) ||
      event.api_attachments?.length
  );
}

function categoryOptionLabels(filterGroups, groupId) {
  const group = filterGroups.find(filterGroup => filterGroup.id === groupId);

  return (group?.options || [])
    .map(option => (typeof option === 'string' ? option : option.label || option.id))
    .filter(Boolean);
}

function hasComplexSchedule(event, timezone = DEFAULT_TIMEZONE) {
  const startKey = event.range_start_key || calendarEventDateKey(event, timezone);
  const endKey = event.range_end_key || calendarEventEndDateKey(event, timezone) || startKey;

  return startKey !== endKey && event.display_mode !== CALENDAR_EVENT_DISPLAY_MODES.dateOnly;
}

function textValue(value) {
  return plainTextFromHtml(value).trim();
}

/**
 * Return today's date key in the calendar display timezone.
 */
export function getCurrentCalendarDateKey(timezone = DEFAULT_TIMEZONE) {
  return formatDateKey(new Date(), timezone);
}

/**
 * Return the current year-month key in the calendar display timezone.
 */
export function getCurrentCalendarMonthYear(timezone = DEFAULT_TIMEZONE) {
  return getCurrentCalendarDateKey(timezone).substring(0, 7);
}

/**
 * Return the active calendar month, falling back to the current month.
 */
export function getCalendarViewingMonth(activeRange, timezone = DEFAULT_TIMEZONE) {
  return activeRange?.start_after?.substring(0, 7) || getCurrentCalendarMonthYear(timezone);
}

/**
 * Report whether the active calendar range is the current month.
 */
export function isCalendarCurrentMonth(activeRange, timezone = DEFAULT_TIMEZONE) {
  return getCalendarViewingMonth(activeRange, timezone) === getCurrentCalendarMonthYear(timezone);
}

/**
 * Return the date range visible in Calendar views for the selected month.
 */
export function calendarVisibleDateRange(
  activeRange,
  timezone = DEFAULT_TIMEZONE,
  showPastEvents = false
) {
  const viewingMonth = getCalendarViewingMonth(activeRange, timezone);
  const monthStartKey = `${viewingMonth}-01`;
  const todayKey = getCurrentCalendarDateKey(timezone);
  const startAfter =
    isCalendarCurrentMonth(activeRange, timezone) && !showPastEvents && todayKey > monthStartKey
      ? todayKey
      : monthStartKey;

  return {
    start_after: startAfter,
    start_before: activeRange?.start_before || addMonths(monthStartKey, 1),
  };
}

/**
 * Sort calendar events by their normalized occurrence start time.
 */
export function sortEventsByStartTime(events) {
  return [...events].sort((a, b) => {
    return calendarEventSortTimestamp(a) - calendarEventSortTimestamp(b);
  });
}

/**
 * Group events by top-level filter group and category.
 */
export function groupEventsByFilterGroup(
  events,
  filterGroups = EVENT_FILTER_GROUPS,
  fallbackGroup = 'Other'
) {
  const grouped = sortEventsByStartTime(events).reduce((groups, event) => {
    const parentGroup = getEventFilterGroup(event.category, filterGroups, fallbackGroup);
    if (!groups[parentGroup]) groups[parentGroup] = {};
    if (!groups[parentGroup][event.category]) groups[parentGroup][event.category] = [];
    groups[parentGroup][event.category].push(event);

    return groups;
  }, {});

  return orderedFilterGroups(grouped, filterGroups);
}

function normalizeCalendarCategory(category) {
  return String(category || '')
    .trim()
    .toLowerCase();
}

function orderedFilterGroups(grouped, filterGroups) {
  const orderedGroups = {};
  const configuredGroups = Object.keys(filterGroups);
  const remainingGroups = Object.keys(grouped).filter(group => !configuredGroups.includes(group));

  for (const groupTitle of [...configuredGroups, ...remainingGroups]) {
    const subcategories = grouped[groupTitle];
    if (!subcategories) continue;

    orderedGroups[groupTitle] = Object.fromEntries(
      orderedSubcategoryEntries(subcategories, filterGroups[groupTitle] || [])
    );
  }

  return orderedGroups;
}

function orderedSubcategoryEntries(subcategories, configuredCategories) {
  const entries = Object.entries(subcategories);
  const categoryByNormalizedName = new Map(
    entries.map(([category]) => [normalizeCalendarCategory(category), category])
  );
  const orderedEntries = [];
  const includedCategories = new Set();

  for (const configuredCategory of configuredCategories) {
    const category = categoryByNormalizedName.get(normalizeCalendarCategory(configuredCategory));

    if (category && !includedCategories.has(category)) {
      orderedEntries.push([category, subcategories[category]]);
      includedCategories.add(category);
    }
  }

  for (const entry of entries) {
    if (!includedCategories.has(entry[0])) {
      orderedEntries.push(entry);
    }
  }

  return orderedEntries;
}

function addMonths(dateKey, months) {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setMonth(date.getMonth() + months);

  return dateToKey(date);
}

function dateToKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}
